import React, {useEffect, useRef, useState} from "react";
import {Button} from "primereact/button";
import {DataView} from "primereact/dataview";
import {Dropdown} from "primereact/dropdown";
import {InputText} from "primereact/inputtext";
import {confirmDialog} from "primereact/confirmdialog";
import {useDispatch} from "react-redux";
import BoxHeader from "../components/box-header";
import MarkdownEditor from "./markdown-editor";
import {ClearableDropdown, RTL_DROPDOWN_PROPS} from "../sales/components/common";
import {relationMutation} from "../sales/sales-utils";
import {setMessage} from "../../system/app-slice";
import {CONTENT_TYPE_UIDS} from "../../system/content-type-registry";
import DatatableEmptyMessage from "../../system/components/datatable-empty-message";
import Icon from "../../system/components/icon";
import {useContentTypeData} from "../../system/use-content-type-data";
import {useTranslate} from "../../system/useTranslate";

const EMPTY_POST = Object.freeze({
    title: "",
    timeToRead: "",
    content: "",
    post_category: null,
    previous_post: null,
    next_post: null,
});

const POSTS_QUERY = {
    sort: "createdAt:DESC",
    populate: "post_category,previous_post,next_post",
};

function relationKey(value) {
    return value?.documentId ?? value?.id ?? null;
}

function CreatePost({postCategories = []}) {
    const translate = useTranslate();
    const dispatch = useDispatch();
    const postsCollection = useContentTypeData(CONTENT_TYPE_UIDS.POST);
    const [postData, setPostData] = useState({...EMPTY_POST});
    const [saving, setSaving] = useState(false);
    const scrollRef = useRef(null);

    useEffect(() => {
        postsCollection.ensure(POSTS_QUERY, {all: true}).catch(() => undefined);
        // The collection methods are stable for this UID.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const notify = (severity, key) => {
        dispatch(setMessage({severity, content: translate(key)}));
    };

    const buildPayload = (publicationStatus) => ({
        title: postData.title.trim(),
        content: postData.content,
        publicationStatus,
        timeToRead: postData.timeToRead,
        post_category: relationMutation(postData.post_category, postData._persistedPostCategory),
        previous_post: relationMutation(postData.previous_post, postData._persistedPreviousPost),
        next_post: relationMutation(postData.next_post, postData._persistedNextPost),
    });

    const selectPost = (post) => {
        setPostData({
            ...EMPTY_POST,
            ...post,
            content: post.content || "",
            _persistedPostCategory: post.post_category || null,
            _persistedPreviousPost: post.previous_post || null,
            _persistedNextPost: post.next_post || null,
        });
        scrollRef.current?.scrollTo({top: 0, behavior: "smooth"});
    };

    const resetToNewPost = () => {
        setPostData({...EMPTY_POST});
    };

    const submitPost = async (publicationStatus) => {
        if (!postData.title.trim()) {
            notify("error", "invalid-post-title");
            return;
        }
        if (!postData.post_category) {
            notify("error", "invalid-post-category");
            return;
        }

        setSaving(true);
        try {
            const payload = buildPayload(publicationStatus);
            const saved = postData.documentId
                ? await postsCollection.update(postData.documentId, payload)
                : await postsCollection.create(payload);
            if (saved) selectPost(saved);
            notify(publicationStatus === "published" ? "success" : "warn", publicationStatus === "published" ? "post-published" : "post-drafted");
            await postsCollection.reload(POSTS_QUERY, {all: true});
        } catch (error) {
            notify("error", "post-save-failed");
        } finally {
            setSaving(false);
        }
    };

    const deletePost = async (post) => {
        try {
            await postsCollection.remove(post.documentId);
            if (post.documentId === postData.documentId) setPostData({...EMPTY_POST});
            notify("success", "post-deleted-successfully");
            await postsCollection.reload(POSTS_QUERY, {all: true});
        } catch (error) {
            notify("error", "post-delete-failed");
        }
    };

    const confirmDelete = (post) => {
        confirmDialog({
            message: translate("delete-post-message").replace("{{POST_TITLE}}", post.title),
            header: translate("delete-post"),
            icon: "pi pi-exclamation-triangle",
            defaultFocus: "reject",
            acceptLabel: translate("delete"),
            rejectLabel: translate("cancel"),
            accept: () => deletePost(post),
        });
    };

    const itemTemplate = (post) => (
        <div className="w-full pb-2">
            <div className="flex align-items-center justify-content-between gap-2 w-full border-1 border-300 border-round p-2">
                <div className="flex flex-column gap-1 text-xs">
                    <div className="flex align-items-center gap-2">
                        <span className="font-bold">{post.title}</span>
                        <span className={`text-xs border-1 border-round p-1 ${post.publicationStatus === "published" ? "border-green-500 text-green-500" : "border-yellow-500 text-yellow-500"}`}>
                            {translate(post.publicationStatus)}
                        </span>
                    </div>
                    <span className="text-400 text-xs">{post.post_category?.title}</span>
                </div>
                <div className="flex gap-2">
                    <Button
                        type="button"
                        onClick={() => selectPost(post)}
                        className="p-button-rounded p-button-text p-button-info w-2rem h-2rem text-xs gap-2"
                        icon={<Icon icon="mi mi:edit" className="text-lg"/>}
                        aria-label={translate("modify-post")}
                    />
                    <Button
                        type="button"
                        onClick={() => confirmDelete(post)}
                        className="p-button-rounded p-button-text p-button-danger w-2rem h-2rem text-xs gap-2"
                        icon={<Icon icon="mi mi:delete" className="text-lg"/>}
                        aria-label={translate("delete-post")}
                    />
                </div>
            </div>
        </div>
    );

    const selectablePosts = postsCollection.items.filter((post) => relationKey(post) !== relationKey(postData));

    return (
        <div className="flex-1 p-3 md:p-4 flex flex-column gap-3 h-full overflow-auto rtl" ref={scrollRef}>
            <div>
                <BoxHeader
                    className="text-lg mt-2 flex-1"
                    title={(
                        <span className="flex align-items-center gap-2">
                            {translate("modify-post")}
                            <Button
                                type="button"
                                className="p-2 text-xs p-button-info p-button-outlined"
                                label={translate("new-post")}
                                onClick={() => resetToNewPost()}
                            />
                        </span>
                    )}
                    left={(
                        <div className="flex gap-2">
                            <Button
                                type="button"
                                label={translate("draft-post")}
                                className="p-button-outlined p-2 text-xs"
                                loading={saving}
                                disabled={saving}
                                onClick={() => submitPost("draft")}
                            />
                            <Button
                                type="button"
                                label={translate("publish-post")}
                                className="p-2 text-xs"
                                loading={saving}
                                disabled={saving}
                                onClick={() => submitPost("published")}
                            />
                        </div>
                    )}
                />
                {postData.documentId && <span className="text-400 english-font text-xs">{postData.documentId}</span>}
            </div>

            <InputText
                className="w-full text-xs"
                placeholder={translate("title")}
                value={postData.title}
                onChange={(event) => setPostData((current) => ({...current, title: event.target.value}))}
            />

            <div className="grid">
                <div className="col-12 md:col-6 flex flex-column gap-2">
                    <label className="text-xs">{translate("time-to-read")}</label>
                    <InputText
                        className="w-full text-xs"
                        placeholder={translate("time-to-read")}
                        value={postData.timeToRead}
                        onChange={(event) => setPostData((current) => ({...current, timeToRead: event.target.value}))}
                    />
                </div>
                <div className="col-12 md:col-6 flex flex-column gap-2">
                    <label className="text-xs">{translate("category")}</label>
                    <Dropdown
                        {...RTL_DROPDOWN_PROPS}
                        className="w-full text-xs"
                        placeholder={translate("category")}
                        options={postCategories}
                        optionLabel="title"
                        dataKey="documentId"
                        value={postData.post_category}
                        onChange={(event) => setPostData((current) => ({...current, post_category: event.value}))}
                    />
                </div>
                <div className="col-12 md:col-6 flex flex-column gap-2">
                    <label className="text-xs">{translate("previous-post")}</label>
                    <ClearableDropdown
                        placeholder={translate("previous-post")}
                        options={selectablePosts}
                        optionLabel="title"
                        dataKey="documentId"
                        value={postData.previous_post}
                        onChange={(event) => setPostData((current) => ({...current, previous_post: event.value}))}
                    />
                </div>
                <div className="col-12 md:col-6 flex flex-column gap-2">
                    <label className="text-xs">{translate("next-post")}</label>
                    <ClearableDropdown
                        placeholder={translate("next-post")}
                        options={selectablePosts}
                        optionLabel="title"
                        dataKey="documentId"
                        value={postData.next_post}
                        onChange={(event) => setPostData((current) => ({...current, next_post: event.value}))}
                    />
                </div>
            </div>

            <MarkdownEditor
                value={postData.content}
                onChange={(content) => setPostData((current) => ({...current, content}))}
            />

            <div className="w-full flex-none mt-4">
                <BoxHeader className="text-base mb-3" title={translate("posts-list")}/>
                <DataView
                    className="w-full"
                    pt={{grid: {className: "flex flex-column w-full"}}}
                    emptyMessage={<DatatableEmptyMessage loading={postsCollection.loading}/>}
                    value={postsCollection.items}
                    layout="list"
                    itemTemplate={itemTemplate}
                />
            </div>
        </div>
    );
}
export default CreatePost;
